import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  ExperienceAnchor,
  ExperienceAnchorType,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@material-ui/core';
import firebase from 'firebase/app';
import { capitalize, compact } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import {
  ItemSorter,
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer,
  RowsRendererScrollContainer
} from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../components/SomethingWentWrong';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { useSort } from '../../../../../hooks/useSort';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import {
  SelectableParticipantWithActors,
  SelectableParticipantWithActorsAnalysis,
  useSelectableParticipantsWithTheirSupportingActors
} from '../../../../../services/experiences/template';
import {
  LoadingValue,
  combineLoadingValues,
  useMappedLoadingValue
} from '../../../../../services/firebase';
import { useOrgById } from '../../../../../services/orgs';
import { now, toMoment } from '../../../../../services/time';
import { getUserValue, toFullName } from '../../../../../services/user';
import { AnchorField } from '../../../components/Anchors';

const HEIGHT = 600;

// show users in a table, with their respective launch date
// calculate launch date for user based on tpl settings

// TODO need to get the tz in here
const getLaunchDateForUser = (
  anchor: ExperienceAnchor,
  user: Doc<IUser>,
  tz: string
) => {
  if (anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
    if (anchor.offset.type === 'ABSOLUTE') {
      // unclear if now is a good reference date here
      const ts = getUserValue(user.data, anchor.field, now());
      if (ts instanceof firebase.firestore.Timestamp) {
        const anchorDate = toMoment(ts).tz(tz);
        const launchDate = anchorDate
          .clone()
          .add(anchor.offset.duration, 'ms')
          .hour(anchor.offset.hour)
          .minutes(anchor.offset.minute || 0)
          .startOf('minute');
        return { anchorDate, launchDate };
      }
    }
  }

  if (anchor.type === ExperienceAnchorType.IMMEDIATE) {
    if (anchor.offset.type === 'ABSOLUTE') {
      const anchorDate = moment().tz(tz);
      const launchDate = anchorDate
        .clone()
        .add(anchor.offset.duration, 'ms')
        .hour(anchor.offset.hour)
        .minutes(anchor.offset.minute || 0)
        .startOf('minute');
      return { anchorDate, launchDate };
    }
  }
  if (anchor.type === ExperienceAnchorType.FIXED) {
    return {
      launchDate: toMoment(anchor.ts).tz(tz),
      anchorDate: null
    };
  }
  console.log('NOT_IMPLEMENTED');
  return null;
};

export const getAnchorLabel = (
  anchor: ExperienceAnchor,
  anchorFields: AnchorField[],
  t: UnsafeTFunction
) => {
  if (anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
    return capitalize(
      anchorFields.find((a) => anchor.field === a.field)?.shortLabel(t) || ''
    );
  }
  return null;
};

type ColumnName = 'name' | 'anchorDate' | 'launchDate';
type D = {
  d: SelectableParticipantWithActors;
  launchDate: moment.Moment;
  anchorDate: moment.Moment | null;
};
type O = {
  anchorDateName: string;
};
type Column = IColumn<D, ColumnName, O>;
const COLUMNS: Column[] = [
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) => toFullName(d.d.participant.data),
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 2
  },
  {
    key: 'anchorDate',
    head: (o) => o?.anchorDateName || 'Anchor Date',
    cell: (d) => d.anchorDate?.format('YYYY/MM/DD HH:mm z'),
    align: 'right',
    sortable: true,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'launchDate',
    head: () => 'Experience Launch Date',
    cell: (d) => d.launchDate.format('YYYY/MM/DD HH:mm z'),
    align: 'right',
    sortable: true,
    width: 150,
    flexGrow: 1
  }
];

const SORTERS: ItemSorters<D, O> = {
  name: {
    key: 'name',
    items: {
      sort: [
        (d) => d.d.participant.data.lastName,
        (d) => d.d.participant.data.preferredName
      ],
      dir: 'asc'
    }
  },
  anchorDate: {
    key: 'anchorDate',
    items: { sort: (d) => d.anchorDate?.valueOf() || 0, dir: 'asc' }
  },
  launchDate: {
    key: 'launchDate',
    items: { sort: (d) => d.launchDate.valueOf(), dir: 'asc' }
  }
};
const DEFAULT_SORTER = SORTERS.name;
const ROW_TO_KEY = (d: D) => d.d.participant.id;

export const useMatchingUsersWithLaunchDates = (
  d: Doc<IExperienceTemplate>
): LoadingValue<D[]> => {
  const { anchor, enrollRule } = d.data;
  const mapFn = useCallback(
    ([res, org]: [
      SelectableParticipantWithActorsAnalysis,
      Doc<IOrganization> | null
    ]) => {
      if (!org) {
        console.error('ORG_NOT_FOUND');
        return EMPTY_ARR;
      }

      const matchingUsers = enrollRule
        ? res.matchingTheTemplateRule
        : EMPTY_ARR;
      console.log(matchingUsers, enrollRule);
      return compact(
        matchingUsers.map((u) => {
          const dates = getLaunchDateForUser(
            anchor,
            u.participant,
            org.data.tz
          );
          return dates ? { d: u, ...dates } : null;
        })
      );
    },
    [anchor, enrollRule]
  );
  return useMappedLoadingValue(
    combineLoadingValues(
      useSelectableParticipantsWithTheirSupportingActors(d),
      useOrgById(d.data.orgId)
    ),
    mapFn,
    true
  );
};

export const EnrollmentPreviewTable = ({
  d,
  ds,
  variant,
  anchorFields
}: {
  d: Doc<IExperienceTemplate>;
  ds: D[];
  variant?: 'plain' | 'contained';
  anchorFields: AnchorField[];
}) => {
  const { t } = useUnsafeTranslation();
  const { sortBy, direction, setSort } = useSort<ItemSorter<D, O>>(
    DEFAULT_SORTER,
    DEFAULT_SORTER.items.dir
  );
  const { anchor } = d.data;
  const anchorLabel = getAnchorLabel(anchor, anchorFields, t) || '';
  const columns = useMemo(
    () =>
      anchorLabel ? COLUMNS : COLUMNS.filter((c) => c.key !== 'anchorDate'),
    [anchorLabel]
  );
  const otherProps = useMemo(
    () => ({
      anchorDateName: anchorLabel
    }),
    [anchorLabel]
  );
  return (
    <RowsRenderer
      variant={variant}
      columns={columns}
      rows={ds}
      rowToKey={ROW_TO_KEY}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={{ sticky: true }}
      onHeadClick={(c, dir) => {
        const sorter = SORTERS[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
    />
  );
};

export const EnrollmentPreviewList = ({
  d,
  anchorFields
}: {
  d: Doc<IExperienceTemplate>;
  anchorFields: AnchorField[];
}) => {
  const [ds, loading, error] = useMatchingUsersWithLaunchDates(d);

  if (loading) {
    return <Loader height={HEIGHT} />;
  }
  if (error) {
    return <SomethingWentWrong height={HEIGHT} />;
  }
  if (!ds) {
    // should never happen
    return null;
  }

  return (
    <RowsRendererScrollContainer height={HEIGHT}>
      <EnrollmentPreviewTable d={d} ds={ds} anchorFields={anchorFields} />
    </RowsRendererScrollContainer>
  );
};

export const EnrollmentPreviewDialog = ({
  d,
  open,
  onClose,
  anchorFields
}: {
  open: boolean;
  onClose: () => void;
  d: Doc<IExperienceTemplate>;
  anchorFields: AnchorField[];
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('Enrollment Preview')}</DialogTitle>
      <DialogContent>
        <EnrollmentPreviewList d={d} anchorFields={anchorFields} />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
